<template>
    <div>
        <div :class="$style['preview__carousel-area']">
            <template v-if="items.length === 1">
                <SwImage
                    :class="[$style['preview__carousel-image'], $style['preview__carousel-image_neo']]"
                    :src="items[0]"
                />

                <span
                    :class="$style['preview__carousel-image-overlay']"
                    @click.stop="openFullScreen"
                />
            </template>

            <CarouselRoot
                v-else-if="items.length"
                ref="carousel"
                :settings="{
                    centerMode: true,
                    itemsToShow: 1,
                    infiniteScroll: items.length > 1,
                    transition: 200,
                    wheelControl: false
                }"
                style="height: 400px"
                @slide="index = $refs.carousel.currentSlide"
            >
                <CarouselSlide
                    v-for="(image, i) in items"
                    :key="i"
                    :index="i"
                    class="slide"
                >
                    <SwImage
                        :class="$style['preview__carousel-image']"
                        :src="image"
                    />

                    <span
                        :class="$style['preview__carousel-image-overlay']"
                        @click.stop="() => openFullScreen(index)"
                    />
                </CarouselSlide>

                <template #hooper-addons>
                    <CarouselPagination v-if="items.length > 1" />
                </template>
            </CarouselRoot>

            <template v-if="items.length > 1">
                <SwButton
                    :class="[$style['preview__carousel-button'], $style['preview__carousel-button_left']]"
                    small
                    @click="slidePrev"
                >
                    <template #leading-icon>
                        <SvgSpriteArrowLeft />
                    </template>
                </SwButton>

                <SwButton
                    :class="[$style['preview__carousel-button'], $style['preview__carousel-button_right']]"
                    small
                    @click="slideNext"
                >
                    <template #leading-icon>
                        <SvgSpriteArrowRight />
                    </template>
                </SwButton>
            </template>
        </div>

        <div
            v-if="items.length > 1"
            :class="$style['preview__thumbnail-list']"
        >
            <span
                v-for="(image, i) in preview"
                :key="i"
                :class="{
                    [$style['preview__thumbnail-list-item']]: true,
                    [$style['preview__thumbnail-list-item_excess']]: exceeds
                }"
                @click.prevent="slideTo(i)"
            >
                <SwImage
                    :src="image"
                    :class="{
                        [$style['preview__thumbnail']]: true,
                        [$style['preview__thumbnail_active']]: i === index
                    }"
                    width="110"
                    height="110"
                    processing="crop"
                />

                <span
                    :class="$style['preview__thumbnail-overlay']"
                    @click.stop="openFullScreen"
                />

                <b @click.stop="openFullScreen">
                    {{ `+${items.length - MAX_COUNT_PREVIEW + 1}` }}
                </b>
            </span>
        </div>
    </div>
</template>

<script>
import {
  Pagination as CarouselPagination,
  Hooper as CarouselRoot,
  Slide as CarouselSlide
} from 'hooper'
import SvgSpriteArrowLeft from './ProductPreview/sprite-arrow-left.svg'
import SvgSpriteArrowRight from './ProductPreview/sprite-arrow-right.svg'

import 'hooper/dist/hooper.css'

const MAX_COUNT_PREVIEW = 4

export default {
  name: 'ProductPreview',

  components: {
    CarouselPagination,
    CarouselRoot,
    CarouselSlide,
    SvgSpriteArrowLeft,
    SvgSpriteArrowRight
  },

  props: {
    items: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    index: 0
  }),

  computed: {
    MAX_COUNT_PREVIEW: () => MAX_COUNT_PREVIEW,

    exceeds () {
      return this.items.length > MAX_COUNT_PREVIEW
    },

    preview () {
      return this.items.slice(0, MAX_COUNT_PREVIEW)
    }
  },

  watch: {
    items: {
      deep: true,
      handler () {
        if (this.$refs.carousel) {
          this.$refs.carousel.slideTo(0)
          this.index = this.$refs.carousel.currentSlide
        }
      }
    }
  },

  methods: {
    openFullScreen () {
      this.$store.commit('gallery/open', {
        images: this.items,
        index: this.index
      })
    },

    slideTo (index) {
      if (this.$refs.carousel) {
        this.$refs.carousel.slideTo(index)
        this.index = this.$refs.carousel.currentSlide
      }
    },

    slidePrev () {
      if (this.$refs.carousel) {
        this.$refs.carousel.slidePrev()
        this.index = this.$refs.carousel.currentSlide >= 0
          ? this.$refs.carousel.currentSlide
          : this.items.length - 1
      }
    },

    slideNext () {
      if (this.$refs.carousel) {
        this.$refs.carousel.slideNext()
        this.index = this.$refs.carousel.currentSlide < this.items.length
          ? this.$refs.carousel.currentSlide
          : 0
      }
    }
  }
}
</script>

<style lang="less" module>
@import (reference) "~@omnica/base/assets/stylesheets/variables.less";
@import (reference) "./assets/Web.less";

.preview {
  &__carousel-area {
    position: relative;
  }

  &__carousel-image {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 400px;
    object-fit: cover;
    margin: 0 auto;

    &_neo {
      margin: auto;
    }

    &:global(.sw-image-placeholder) {
      width: 100%;
      aspect-ratio: 1;
    }
  }

  &__carousel-image-overlay {
    display: block;
    cursor: zoom-in;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    @media screen and (max-width: @bp-tablet) {
      display: none;
    }
  }

  &__carousel-button {
    transform: translateY(-50%);
    position: absolute;
    top: 50%;
    z-index: 1;
    @media screen and (max-width: @bp-tablet) {
      display: none !important;
    }

    &_left { left: 12px; }
    &_right { right: 12px; }
  }

  &__thumbnail-list {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    padding: 1px 1px;
    overflow: hidden;
    margin: 15px 0;

    @media screen and (max-width: @bp-mobile-big) {
      display: none;
    }
  }

  &__thumbnail-overlay,
  &__thumbnail-list-item b {
    display: none;
  }

  &__thumbnail-list-item:nth-child(4)&__thumbnail-list-item_excess {
    position: relative;
  }

  &__thumbnail-list-item:nth-child(4)&__thumbnail-list-item_excess &__thumbnail-overlay {
    display: block;
    width: 100%;
    height: 100%;
    background-color: @black-200;
    opacity: 0.4;
    cursor: pointer;
    position: absolute;
    top: 0;
    left: 0;
  }

  &__thumbnail-list-item:nth-child(4)&__thumbnail-list-item_excess b {
    display: inline;
    font-size: 24px;
    line-height: 32px;
    color: #FFFFFF;
    cursor: pointer;
    transform: translate(-50%, -50%);
    position: absolute;
    top: 50%;
    left: 50%;
  }

  &__thumbnail-list-item + &__thumbnail-list-item {
    margin-left: 16px;
  }

  &__thumbnail-list-item &__thumbnail {
    flex: 1 0 25%;
    width: 110px;
    height: 110px;
    max-width: 110px;
    cursor: pointer;
    object-fit: cover;
    margin: 0;

    &_active {
      border: 1px solid var(--sw-color-main-500);
      box-sizing: border-box;
      cursor: default;
    }
  }
}
</style>

<style lang="less">
.hooper-list {
  height: 100% !important;
}

.hooper-slide {
  display: flex;
  padding: 1px !important;
  margin: auto !important;
  position: relative;
}

.hooper-indicators {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;
  position: absolute;
  bottom: 32px;
  width: 100%;
  z-index: 1;

  button {
    width: 8px;
    height: 8px;
    padding: 0;
    background: var(--sw-color-main-200);
    border: none;
    border-radius: 2px;
    margin: 4px;
  }

  .is-active {
    background: var(--sw-color-main-500) !important;
  }
}
</style>
